"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ChevronRight, ArrowUpRight, ShieldCheck, Terminal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface JourneyStep {
  readonly id: string;
  readonly code: string;
  readonly titleEn: string;
  readonly titleVi: string;
  readonly descEn: string;
  readonly descVi: string;
}
const JOURNEY_STEPS: readonly JourneyStep[] = [{
  id: "step-1",
  code: "01",
  titleEn: "LEARN",
  titleVi: "HỌC HỎI",
  descEn: "Core mechanics & code",
  descVi: "Cơ khí & lập trình"
}, {
  id: "step-2",
  code: "02",
  titleEn: "BUILD",
  titleVi: "CHẾ TẠO",
  descEn: "Hardware assembly",
  descVi: "Lắp ráp phần cứng"
}, {
  id: "step-3",
  code: "03",
  titleEn: "SOLVE",
  titleVi: "GIẢI QUYẾT",
  descEn: "Algorithmic logic",
  descVi: "Tư duy thuật toán"
}, {
  id: "step-4",
  code: "04",
  titleEn: "TEAM UP",
  titleVi: "ĐỒNG ĐỘI",
  descEn: "Role coordination",
  descVi: "Phân vai & phối hợp"
}, {
  id: "step-5",
  code: "05",
  titleEn: "TEST",
  titleVi: "THỬ NGHIỆM",
  descEn: "Mat simulation runs",
  descVi: "Chạy thử trên sa bàn"
}, {
  id: "step-6",
  code: "06",
  titleEn: "PRESENT",
  titleVi: "THUYẾT TRÌNH",
  descEn: "Engineering defense",
  descVi: "Bảo vệ giải pháp"
}, {
  id: "step-7",
  code: "07",
  titleEn: "COMPETE",
  titleVi: "TRANH TÀI",
  descEn: "Live tournament arena",
  descVi: "Thi đấu thực tế"
}, {
  id: "step-8",
  code: "08",
  titleEn: "IMPROVE",
  titleVi: "TỐI ƯU",
  descEn: "Post-match telemetry",
  descVi: "Phân tích & cải tiến"
}];
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
export default function CompetitionsHero() {
  const [lang, setLang] = useState<"en" | "vi">("en");
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const readLang = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "vi" || stored === "en") {
          setLang(stored);
        }
      } catch {
        // Fallback gracefully if storage access is restricted
      }
    };
    readLang();
    const handleLangChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: string;
      }>;
      if (customEvent?.detail?.lang === "vi" || customEvent?.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      } else {
        readLang();
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const copy = {
    badge: lang === "vi" ? "ĐẤU TRƯỜNG KỸ THUẬT ROBOTICS" : "COMPETITION ENGINEERING ARENA",
    headline: lang === "vi" ? "Khởi nguồn kỹ thuật. Vững bước tranh tài." : "Engineered to compete. Built to innovate.",
    subhead: lang === "vi" ? "Chuẩn bị cho học viên từ 6-16 tuổi tham gia các giải đấu robotics tiêu chuẩn quốc tế như FIRST LEGO League, World Robot Olympiad và VEX Robotics thông qua phương pháp kỹ thuật thực nghiệm, kiểm thử hệ thống và tinh thần đồng đội." : "Equipping young engineers aged 6–16 for major robotics competitions, including FIRST LEGO League, World Robot Olympiad, and VEX Robotics divisions, through hands-on mechanical builds, sensor integration, and systematic testing.",
    disclaimer: lang === "vi" ? "ROBOTO huấn luyện kỹ thuật độc lập theo chuẩn đề thi các đấu trường mở quốc tế, không tuyên bố là đối tác đại diện chính thức." : "Independent preparation curriculum benchmarking skills against international tournament formats. Mentions are descriptive of target categories.",
    primaryCta: lang === "vi" ? "Đăng ký buổi đánh giá thử" : "Book an evaluation session",
    secondaryCta: lang === "vi" ? "Xem các hạng mục thi đấu" : "Explore competition tracks",
    journeyEyebrow: lang === "vi" ? "QUY TRÌNH KỸ THUẬT 8 GIAI ĐOẠN" : "8-PHASE COMPETITOR ROADMAP",
    journeySummary: lang === "vi" ? "Từ nền tảng cơ khí đến sa bàn thi đấu" : "From mechanical foundations to the final tournament arena"
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="competitionshero" data-nav="dark" className="relative overflow-hidden bg-foreground text-background pt-24 pb-16 md:pt-32 md:pb-24 border-b border-background/10">
      {/* Blueprint Coordinate Grid and Circuit Pattern Overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-15">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="blueprint-grid-hero" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" strokeWidth="0.75" strokeOpacity="0.4" />
              <circle cx="32" cy="32" r="1" fill="currentColor" fillOpacity="0.6" />
            </pattern>
            <pattern id="blueprint-dots-hero" width="128" height="128" patternUnits="userSpaceOnUse">
              <circle cx="64" cy="64" r="2" fill="currentColor" fillOpacity="0.5" />
              <path d="M 64 56 L 64 72 M 56 64 L 72 64" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#blueprint-grid-hero)" />
          <rect width="100%" height="100%" fill="url(#blueprint-dots-hero)" />
        </svg>
      </div>

      {/* Decorative Gradient Glows for High-Energy Vibe */}
      <div aria-hidden="true" className="pointer-events-none absolute top-12 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-accent/15 blur-3xl rounded-full" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 -right-24 w-80 h-80 bg-primary/20 blur-3xl rounded-full" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Floating Mascot (Static Decorative Visual) */}
        <div aria-hidden="true" className="pointer-events-none select-none hidden lg:block absolute top-2 lg:top-4 left-2 lg:left-4 xl:left-8 2xl:left-12 z-0">
          <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" loading="lazy" className="w-20 h-20 xl:w-28 xl:h-28 2xl:w-32 2xl:h-32 object-contain bg-transparent pointer-events-none select-none drop-shadow-md opacity-85 xl:opacity-100" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto">
          {/* Technical Eyebrow Badge */}
          <motion.div variants={itemVariants} className="mb-6">
            <Badge variant="outline" className="inline-flex items-center gap-2 rounded-full border-accent/40 bg-background/5 px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-accent backdrop-blur-sm">
              <Terminal className="size-3.5 text-accent animate-pulse" />
              <span>{copy.badge}</span>
              <span className="size-1.5 rounded-full bg-accent" />
            </Badge>
          </motion.div>

          {/* Main Display Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-background leading-[1.08] mb-6">
            {copy.headline}
          </motion.h1>

          {/* Editorial Lead Paragraph */}
          <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-background/85 leading-relaxed max-w-3xl mb-8 font-sans">
            {copy.subhead}
          </motion.p>

          {/* Dual Action Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto mb-6">
            <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold px-8 py-6 rounded-md shadow-lg shadow-accent/20 hover:bg-accent/90 transition-[color,background-color,box-shadow]">
              <Link to="/FreeTrialContactPage" className="inline-flex items-center gap-2">
                <span>{copy.primaryCta}</span>
                <ChevronRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-background/25 bg-background/5 text-background font-medium px-6 py-6 rounded-md backdrop-blur-xs hover:bg-background/15 hover:border-background/40 hover:text-background transition-[color,background-color,border-color]">
              <Link to="#competitions-we-prepare-for" className="inline-flex items-center gap-2">
                <span>{copy.secondaryCta}</span>
                <ArrowUpRight className="size-4 text-background/70" />
              </Link>
            </Button>
          </motion.div>

          {/* Descriptive Clarification / Benchmark Note */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 text-xs text-background/60 max-w-2xl px-4 text-center mb-16">
            <ShieldCheck className="size-3.5 shrink-0 text-accent/80" />
            <span>{copy.disclaimer}</span>
          </motion.div>
        </motion.div>

        {/* Competitor Engineering Journey (8-Phase Strip) */}
        <motion.div variants={itemVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-4 pt-10 border-t border-background/15">
          {/* Header of the Journey Strip */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-8">
            <div className="flex items-center gap-2">
              <Cpu className="size-4 text-accent" />
              <span className="text-xs font-mono font-semibold tracking-wider uppercase text-accent">
                {copy.journeyEyebrow}
              </span>
            </div>
            <p className="text-xs text-background/70 font-sans">
              {copy.journeySummary}
            </p>
          </div>

          {/* The 8 Phases Rail Grid */}
          <div className="relative">
            {/* Fine Specification Connecting Rail (Desktop view) */}
            <div aria-hidden="true" className="hidden lg:block absolute top-[28px] left-6 right-6 h-[1px] bg-background/20 z-0" />

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4 relative z-10">
              {JOURNEY_STEPS.map((step, i) => <div key={step.id} data-index={i} className="group relative flex flex-col min-w-0 p-3 rounded-lg border border-background/10 bg-background/5 backdrop-blur-xs transition-[border-color,background-color] hover:border-accent/40 hover:bg-background/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-2xs font-semibold text-accent/90 px-1.5 py-0.5 rounded bg-background/10">
                      {step.code}
                    </span>
                    <span className="size-2 rounded-full bg-accent/60 group-hover:bg-accent group-hover:scale-125 transition-transform" />
                  </div>

                  <h3 className="text-xs sm:text-sm font-bold font-serif tracking-wide text-background mb-1">
                    {lang === "vi" ? step.titleVi : step.titleEn}
                  </h3>

                  <p className="text-2xs text-background/70 leading-normal line-clamp-2">
                    {lang === "vi" ? step.descVi : step.descEn}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}